import { on } from "delegated-events";
import { slideUp, slideDown } from "@common/slide";
import scrollReveal from "@scripts/components/scroll-reveal";
import easydropdown from "easydropdown-a11y";
import "@styles/components/dropdown.css";
import ajax from "@common/ajax";
import { generateQueryString } from "@common/util";
import pillList from "@scripts/components/pill-list";
import "@styles/components/slide-toggle.css";

const panel = document.getElementById("page-requirements");
const headline = document.querySelector(".panel-one-headline");

const setupLogoGrid = () => {
  const form = document.querySelector(".filter-form");

  const setupDropdown = (el) => {
    if (el.tagName !== "SELECT") {
      return;
    }

    const opts = {
      behavior: {
        openOnFocus: true,
        maxVisibleItems: 10,
        liveUpdates: true,
        useNativeUiOnMobile: false,
      },
    };

    easydropdown(el, opts);
  };

  const handleFilterChange = (e) => {
    e.preventDefault();

    const params = new FormData(form);
    const queryString = generateQueryString(params);
    const url = queryString.size ? `${form.action}?${queryString}` : form.action;

    window.history.replaceState({}, "", url);

    updateResults();
  };

  const handleFilterClear = () => {
    form.reset();
    form.querySelectorAll("select").forEach((x) => (x.value = ""));

    window.history.replaceState({}, "", form.action);

    updateResults();
  };

  const updateResults = async () => {
    const pageContent = document.querySelector("[data-page-content]");

    pageContent.classList.add("is-loading");

    const res = await ajax.get(window.location.href).json();

    pageContent.innerHTML = res.page;
    pageContent.classList.remove("is-loading");

    if (res.isFiltered) {
      form.classList.add("is-filtered");
    } else {
      form.classList.remove("is-filtered");
    }

    const pageHeaderTitle = document.querySelector(".page-header-title");

    if (res.categoryPageHeaderTitle) {
      pageHeaderTitle.innerHTML = res.categoryPageHeaderTitle;
    } else {
      pageHeaderTitle.innerHTML = pageHeaderTitle.dataset.pageTitle;
    }
  };

  if (form) {
    form.querySelectorAll("select:not([multiple])").forEach((el) => setupDropdown(el));

    const filterPills = form.querySelector("[data-pill-list]");

    if (filterPills) {
      pillList.init(filterPills);
    }

    on("change", ".filter-form select", handleFilterChange);
    on("change", '.filter-form input[type="radio"]', (e) => {
      handleFilterChange(e);
      pillList.animatePillMarker(filterPills);
    });
    on("submit", ".filter-form", handleFilterChange);
    on("click", ".filter-form-clear", () => {
      handleFilterClear();
      pillList.animatePillMarker(filterPills);
      form.classList.remove("is-searching");
    });

    const handleFilterVisibility = () => {
      if (window.matchMedia("(min-width: 768px)").matches) {
        form.querySelectorAll("select").forEach((el) => (el.disabled = true));
        form.querySelectorAll('input[type="radio"]').forEach((el) => (el.disabled = false));
      } else {
        form.querySelectorAll("select").forEach((el) => (el.disabled = false));
        form.querySelectorAll('input[type="radio"]').forEach((el) => (el.disabled = true));
      }
    };

    handleFilterVisibility();

    window.addEventListener("resize", handleFilterVisibility);
  }
};

const handlePanelOneHeadline = () => {
  const targetContainer = document.querySelector(".panel-one-headline-container");
  const targetContainerPadding = parseInt(getComputedStyle(targetContainer).getPropertyValue("padding-top"));
  const panelGrid = document.querySelector(".panel-grid");
  const height = headline.offsetHeight;

  panelGrid.style.marginTop = `${(height + targetContainerPadding) * -1}px`;
};

const openPanel = async () => {
  panel.classList.add("absolute", "invisible");
  panel.classList.remove("hidden");
  handlePanelOneHeadline();
  panel.setAttribute("data-sr-group", true);

  panel.classList.add("hidden");
  panel.classList.remove("absolute", "invisible");

  await slideDown(panel);

  scrollReveal.reveal(panel);
  window.addEventListener("resize", handlePanelOneHeadline);
};

const closePanel = async () => {
  window.removeEventListener("resize", handlePanelOneHeadline);

  headline.style.position = "relative";
  headline.style.top = "0";

  await slideUp(panel, 250);
  panel.style = null;
  headline.style = null;

  panel.querySelectorAll("[data-sr]").forEach((el) => {
    el.style = null;
  });
};

const init = () => {
  on("click", ".panel-list-toggle-all", (e) => {
    const holder = e.target.closest(".page-panel");
    const items = holder.querySelectorAll(".panel-list-item");
    const type = e.target.dataset.type;

    if (type === "open") {
      items.forEach(async (item) => {
        const target = document.getElementById(item.getAttribute("aria-controls"));
        item.setAttribute("aria-expanded", "true");

        await slideDown(target);
      });
    }

    if (type === "close") {
      items.forEach(async (item) => {
        const target = document.getElementById(item.getAttribute("aria-controls"));
        item.setAttribute("aria-expanded", "false");

        await slideUp(target);
      });
    }
  });

  const items = document.querySelectorAll(".page-panel .panel-list-item");

  if (items) {
    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === "attributes" && mutation.attributeName === "aria-expanded") {
          const opened = document.querySelectorAll('.panel-list-item[aria-expanded="true"]').length;
          const toggle = document.querySelector(".panel-list-toggle-all");

          if (opened > 0) {
            toggle.textContent = "Close All";
            toggle.setAttribute("data-type", "close");
          }

          if (opened === 0) {
            toggle.textContent = "Open All";
            toggle.setAttribute("data-type", "open");
          }
        }
      }
    });

    items.forEach((item) => {
      observer.observe(item, { attributes: true });
    });
  }

  const scrollPixel = document.querySelector(".panel-scroll-pixel");

  if (scrollPixel) {
    const screen = document.querySelector(".panel-one-headline-screen");
    const siteHeaderOffset = document.querySelector(".site-header").offsetHeight * -1;

    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.intersectionRatio === 0) {
          screen.classList.remove("opacity-0");
        } else {
          screen.classList.add("opacity-0");
        }
      },
      {
        rootMargin: `${siteHeaderOffset}px 0px 0px 0px`,
        threshold: [1],
      },
    );

    observer.observe(document.querySelector(".panel-scroll-pixel"));
  }

  on("click", ".page-requirements-toggle", (e) => {
    const trigger = e.currentTarget;

    if (trigger.getAttribute("aria-expanded") === "false") {
      trigger.setAttribute("aria-expanded", "true");
      openPanel();
    } else {
      trigger.setAttribute("aria-expanded", "false");
      closePanel();
    }
  });

  setupLogoGrid();
};

init();
